/**
 * Quote Currency Utils - OFFICIAL CURRENCY FORMATTING STANDARD
 * 
 * 🚨 CRITICAL: Use ONLY these functions for user-facing currency displays
 * 
 * ✅ ALWAYS USE:
 * - formatAmountWithFinancialPrecision() for displaying amounts
 * - useQuoteCurrency() hook for component-level currency operations
 * - convertCurrency() for precise conversion
 * 
 * ❌ NEVER USE:
 * - currencyService.formatAmount() (causes rounding issues)
 * - Math.round() in currency functions (precision loss)
 * - Custom formatting without these standards
 * 
 * Problem solved: ₹399 NPR conversion rounding issues eliminated
 * All functions log with [convert-user] tag for debugging
 */

import { useCallback, useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { getDestinationCurrency, getOriginCurrency } from '@/utils/originCurrency';
import { currencyService } from '@/services/CurrencyService';
import { convertAmountWithSessionLock } from '@/services/SessionCurrencyService';
import { trackCurrencyConversion, logTargetAmount } from '@/utils/currencyDebugUtils';
import { getCountryFromTimezone, getCurrencyFromTimezone } from '@/utils/simpleLocationDetection';

/**
 * Get user profile for currency preferences
 * EXACT same logic from quote page
 * FIXED: Handle dev mode users properly
 */
export function useUserProfile() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['user_profile', user?.id],
    queryFn: async () => {
      if (!user?.id) return null;

      // Skip Supabase profile fetching in dev mode for mock users
      const isDevMode = import.meta.env.VITE_DEV_MODE === 'true';
      const isDevUser = user.id.startsWith('dev-');

      if (isDevMode && isDevUser) {
        // Return mock profile for dev users
        return {
          id: user.id,
          email: user.email || 'dev@iwishbag.com',
          full_name: user.user_metadata?.name || 'Dev User',
          phone: user.phone || null,
          country: 'US',
          preferred_display_currency: 'USD',
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        };
      }

      // For AWS migration: Use default profile based on user auth data
      // TODO: In the future, integrate with AWS RDS for user profiles
      console.log(`[useUserProfile] Using default profile for AWS migration`);
      return {
        id: user.id,
        email: user.email || '',
        full_name: user.user_metadata?.name || 'Guest User',
        phone: user.phone || null,
        country: 'US', // Default country - could be enhanced with location detection
        preferred_display_currency: 'USD', // Default currency - could be enhanced with timezone detection
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };
    },
    enabled: !!user?.id,
    // Make it more resilient with retry settings
    retry: 1,
    retryDelay: 500,
  });
}

/**
 * Get display currency - SIMPLIFIED: Timezone-based detection
 * Priority: User profile preference → Timezone detection → Quote destination → USD fallback
 *
 * For multi-currency payments, user choice is primary!
 */
export function useDisplayCurrency(quote?: any) {
  const { user } = useAuth();
  const { data: userProfile, isLoading: isLoadingProfile } = useUserProfile();

  const getDisplayCurrency = useCallback(() => {
    
    // ALWAYS prioritize user's preferred payment currency
    if (user?.id && userProfile?.preferred_display_currency) {
      console.log(`[convert-user] ✅ Using user profile preference: ${userProfile.preferred_display_currency}`);
      return userProfile.preferred_display_currency;
    }


    // For both guests AND logged-in users without preferences, use timezone detection
    try {
      const detectedCurrency = getCurrencyFromTimezone();
      console.log(`[convert-user] 🌍 ${user ? 'Logged-in user' : 'Guest user'} - timezone detection → ${detectedCurrency} (instant)`);
      return detectedCurrency;
    } catch (error) {
      console.warn(`[convert-user] ⚠️ Timezone detection failed:`, error);
    }

    // Second priority: Use quote destination if available (for both guests and logged-in users)
    if (quote?.destination_country) {
      const destinationCurrency = getDestinationCurrency(quote.destination_country);
      // If it's a major currency, use it; otherwise default to USD
      const majorCurrencies = ['USD', 'EUR', 'GBP', 'INR', 'NPR', 'AUD', 'SGD', 'JPY'];
      const finalCurrency = majorCurrencies.includes(destinationCurrency) ? destinationCurrency : 'USD';
      console.log(`[convert-user] 👤 Quote destination ${quote.destination_country} → ${destinationCurrency} → ${finalCurrency} (major currency check)`);
      return finalCurrency;
    }

    console.log(`[convert-user] 💵 Fallback to USD (no user preference, timezone detection failed, and no quote context)`);
    return 'USD';
  }, [user?.id, userProfile?.preferred_display_currency, quote?.destination_country, isLoadingProfile]);

  return {
    displayCurrency: getDisplayCurrency(),
    isLoading: isLoadingProfile,
    isReady: !isLoadingProfile
  };
}

/**
 * Currency conversion with financial precision - EXACT same from quote page
 */
export const convertCurrency = async (amount: number, fromCurrency: string, toCurrency: string): Promise<number> => {
  
  if (fromCurrency === toCurrency) {
    return amount;
  }
  
  try {
    // Use session-locked conversion for consistency
    const converted = await convertAmountWithSessionLock(amount, fromCurrency, toCurrency);
    
    
    // Debug logging for currency conversion
    if (isNaN(converted)) {
      console.warn(`[convert-user] ❌ NaN detected in conversion:`, {
        amount,
        fromCurrency,
        toCurrency,
        converted,
        stackTrace: new Error().stack
      });
    }
    
    // Let formatAmount() handle proper rounding based on currency rules
    const result = converted;
    
    // Final NaN check
    if (isNaN(result)) {
      console.warn(`[convert-user] ❌ NaN detected in final result:`, {
        amount,
        fromCurrency,
        toCurrency,
        converted,
        result
      });
      return 0; // Fallback to 0 instead of NaN
    }
    
      return result;
  } catch (error) {
    console.warn(`[convert-user] ❌ Currency conversion failed ${fromCurrency}->${toCurrency}:`, error);
    return amount;
  }
};

/**
 * Format amount with simple financial precision (2 decimals) like quote page
 * Avoids CurrencyService's smart rounding that can show 0 decimals for large amounts
 */
export const formatAmountWithFinancialPrecision = (amount: number, currencyCode: string): string => {
  
  const currency = currencyService.getCurrencySymbol(currencyCode);
  
  
  // Debug logging for NaN issues
  if (amount !== amount || isNaN(amount) || amount == null) {
    console.warn(`[convert-user] ❌ Invalid amount detected:`, { 
      amount, 
      type: typeof amount, 
      isNaN: isNaN(amount),
      currencyCode,
      stackTrace: new Error().stack 
    });
  }
  
  // Handle null, undefined, or NaN values
  const safeAmount = amount == null || isNaN(amount) ? 0 : amount;
  
  
  // Let the locale formatter handle proper precision
  const preciseAmount = safeAmount;
  
  // Format with thousands separators but keep exactly 2 decimal places
  const formatted = preciseAmount.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    useGrouping: true
  });
  
  const finalFormatted = `${currency}${formatted}`;
  
  return finalFormatted;
};

/**
 * Format amount with conversion - EXACT same logic from quote page
 * Uses simple 2-decimal financial precision like quote page (not smart rounding)
 */
export function useFormatAmountWithConversion(displayCurrency: string) {
  const formatAmountWithConversion = useCallback(async (
    amount: number, 
    sourceCurrency?: string
  ): Promise<string> => {
    const fromCurrency = sourceCurrency || 'USD';
    
    
    if (fromCurrency === displayCurrency) {
      // Use simple financial precision formatting instead of smart rounding
      return formatAmountWithFinancialPrecision(amount, displayCurrency);
    }
    
    try {
      const convertedAmount = await convertCurrency(amount, fromCurrency, displayCurrency);
      // Use simple financial precision formatting instead of smart rounding  
      return formatAmountWithFinancialPrecision(convertedAmount, displayCurrency);
    } catch (error) {
      console.warn('[convert-user] ❌ Currency formatting failed, using original:', error);
      return formatAmountWithFinancialPrecision(amount, fromCurrency);
    }
  }, [displayCurrency]);

  return formatAmountWithConversion;
}

/**
 * Quote price formatter - EXACT same logic from quote page
 * Uses simple financial precision to match quote page display
 */
export function useQuotePriceFormatter(quote: any, displayCurrency: string) {
  const formatItemQuotePrice = useCallback(async (itemQuotePrice: number): Promise<string> => {
    try {
      const sourceCurrency = quote.origin_country ? 
        (await import('@/utils/originCurrency')).getOriginCurrency(quote.origin_country) : 'USD';
      
      if (sourceCurrency === displayCurrency) {
        return formatAmountWithFinancialPrecision(itemQuotePrice, displayCurrency);
      }
      
      const convertedPrice = await convertCurrency(itemQuotePrice, sourceCurrency, displayCurrency);
      return formatAmountWithFinancialPrecision(convertedPrice, displayCurrency);
    } catch (error) {
      console.warn('Failed to convert item quote price:', error);
      const fallbackCurrency = quote.origin_country ? 
        (await import('@/utils/originCurrency')).getOriginCurrency(quote.origin_country) : 'USD';
      return formatAmountWithFinancialPrecision(itemQuotePrice, fallbackCurrency);
    }
  }, [quote, displayCurrency]);

  return formatItemQuotePrice;
}

/**
 * Helper function to get the quote total with fallback to calculation data
 * Fixes issue where quote fields are zero but calculation data has correct amount
 */
const getQuoteTotal = (quote: any): number => {
  // First, try quote fields
  let total = quote.total_quote_origincurrency || quote.total_origin_currency || quote.origin_total_amount;
  
  // If quote fields are zero/null, fallback to calculation data
  if (!total || total <= 0) {
    total = quote.calculation_data?.calculation_steps?.total_origin_currency || 
           quote.calculation_data?.calculation_steps?.total_quote_origincurrency;
  }
  
  return total || 0;
};

/**
 * Calculate quote total in target currency
 * Extracted from ProfessionalBreakdown component for consistency across app
 * 
 * @param quote - Quote object with calculation data
 * @param targetCurrency - Currency to convert to (INR, NPR, USD, etc.)
 * @returns Promise<number> - Total amount in target currency
 */
export async function calculateQuoteTotal(
  quote: any, 
  targetCurrency: string
): Promise<number> {
  
  
  try {
    // Extract working logic from ProfessionalBreakdown component
    const steps = quote.calculation_data?.calculation_steps || {};
    const originCurrency = quote.origin_country ? getOriginCurrency(quote.origin_country) : 'USD';
    
    console.log(`[convert-user] 📋 Quote data analysis:`, {
      quoteId: quote.id,
      originCountry: quote.origin_country,
      originCurrency,
      targetCurrency,
      hasCalculationSteps: !!steps,
      stepsKeys: Object.keys(steps)
    });
    
    // 🔥 CRITICAL FIX: Use calculation_data.data.totals.total (complete amount with all fees)
    // Fallback to calculation steps, then to incomplete quote fields
    const calculationDataTotal = quote.calculation_data?.data?.totals?.total;
    
    // 🔥 FIX: Also extract the currency from calculation_data if available
    const calculationDataCurrency = quote.calculation_data?.data?.currency;
    
    const baseAmount = calculationDataTotal || steps.total_origin_currency || getQuoteTotal(quote);
    
    const quoteTotal = getQuoteTotal(quote);
    const stepsTotal = steps.total_origin_currency;
    
    console.log(`[convert-user] 💰 Base amount selection:`, {
      fromCalculationData: calculationDataTotal,
      fromSteps: stepsTotal,
      fromQuoteTotal: quoteTotal,
      selectedValue: baseAmount,
      selectionReason: calculationDataTotal ? 'using_calculation_data_total' : 
                      stepsTotal ? 'using_steps_total' : 'fallback_to_quote_field',
      originCurrency,
      calculationDataCurrency,
      currencyMismatch: calculationDataCurrency && calculationDataCurrency !== originCurrency,
      debugNote: calculationDataCurrency && calculationDataCurrency !== originCurrency ? 
        `Currency mismatch: origin(${originCurrency}) vs calculation(${calculationDataCurrency}) - may cause conversion issues` : 
        'Currency alignment looks correct'
    });
    
    if (!baseAmount || baseAmount <= 0) {
      console.warn(`[convert-user] ⚠️ Invalid base amount: ${baseAmount}`);
      return 0;
    }
    
    // 🔥 CRITICAL FIX: Use the actual currency from calculation data if available
    // This fixes the JPY vs INR currency mismatch issue causing zero totals
    const actualSourceCurrency = calculationDataCurrency || originCurrency;
    
    // Use base amount as-is, avoid double rounding
    const preciseBaseAmount = baseAmount;
    
    console.log(`[convert-user] 🔧 Currency source correction:`, {
      originalOriginCurrency: originCurrency,
      calculationDataCurrency,
      actualSourceCurrency,
      note: calculationDataCurrency ? 'Using calculation data currency (more accurate)' : 'Using origin country currency'
    });
    
    // Convert to target currency if needed
    if (actualSourceCurrency === targetCurrency) {
      console.log(`[convert-user] ✅ Same currency - returning base amount: ${preciseBaseAmount} ${targetCurrency}`);
      return preciseBaseAmount;
    }
    
    
    // Use SESSION-LOCKED conversion to ensure consistency across all components
    const convertedAmount = await convertAmountWithSessionLock(preciseBaseAmount, actualSourceCurrency, targetCurrency);
    
    // DEBUG: Check if conversion seems wrong
    const conversionRate = convertedAmount / preciseBaseAmount;
    
    console.log(`[convert-user] 📊 Applied exchange rate: ${conversionRate} (${actualSourceCurrency}→${targetCurrency})`);
    
    // Validate conversion makes sense for JPY->INR (should be around 0.6, not 8+)
    if (actualSourceCurrency === 'JPY' && targetCurrency === 'INR' && conversionRate > 2) {
      console.warn(`[convert-user] ⚠️ SUSPICIOUS CONVERSION RATE: ${conversionRate} for JPY→INR (expected ~0.6)`);
    }
    
    // Let the formatter handle final precision
    const finalAmount = convertedAmount;
    
    // Track this conversion for debugging
    const exchangeRate = convertedAmount / preciseBaseAmount;
    console.log(`[convert-user] 📈 Exchange rate applied: ${exchangeRate} (${actualSourceCurrency}→${targetCurrency})`);
    
    await trackCurrencyConversion(
      'calculateQuoteTotal',
      preciseBaseAmount,
      actualSourceCurrency,
      targetCurrency,
      finalAmount,
      exchangeRate,
      'session_locked'
    );
    
    // Log if this matches our target amounts
    logTargetAmount(finalAmount, targetCurrency, 'calculateQuoteTotal');
    
    console.log(`[convert-user] ✅ Final quote total: ${finalAmount} ${targetCurrency}`);
    return finalAmount;
    
  } catch (error) {
    console.error('[convert-user] ❌ Quote calculation failed:', error);
    // Fallback to getQuoteTotal helper
    const fallbackAmount = getQuoteTotal(quote);
    console.log(`[convert-user] 🔧 Using fallback amount: ${fallbackAmount}`);
    return fallbackAmount;
  }
}

/**
 * Hook that provides all quote currency functions
 * Use this in cart/checkout components instead of useCartCurrency
 */
export function useQuoteCurrency(quote?: any) {
  const { displayCurrency, isLoading, isReady } = useDisplayCurrency(quote);
  const formatAmountWithConversion = useFormatAmountWithConversion(displayCurrency);
  const formatItemQuotePrice = useQuotePriceFormatter(quote, displayCurrency);
  
  return {
    displayCurrency,
    isLoading,
    isReady,
    formatAmountWithConversion,
    formatItemQuotePrice,
    convertCurrency,
    calculateQuoteTotal
  };
}